import { useEffect, useState } from 'react';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import pageCatalog from '@/i18n/ns/connections-page';
import {
  CatalogueCard,
  connectedTileAction,
  tileStateFor,
  type CatalogueConnection,
  type CatalogueTileState,
} from '../catalogue-card';
import type { ShopifyDevAppEntry } from '../pipedream-catalogue-api';
import { applyConnectChoice } from './connect-choice';
import { ShopifyDevAppDialog } from './shopify-dialog';
import { useConnectionContextKey } from '../connect-access/use-current-request';

/** `?setup=shopify` (from a chat offer to connect Shopify) opens the Developer App's sheet. */
export function shopifySetupRequested() {
  const query = typeof location === 'undefined' ? '' : (location.hash.split('?')[1] ?? '');
  return new URLSearchParams(query).get('setup') === 'shopify';
}

/**
 * The Shopify Developer App's connect sheet, opened from its catalogue card. The new store's own
 * grant comes back with the create response, so the connect-time access choice applies at once.
 */
export function useShopifyDevAppSheet({
  companyName,
  canConnectCompany,
  onConnected,
}: {
  companyName: string;
  canConnectCompany: boolean;
  /** Called with the notice to show once the store is saved (and its access applied when possible). */
  onConnected: (notice: string) => void;
}) {
  // The sheet belongs to the account and role it was opened for: a switch closes it, so a late
  // create can never apply an earlier context's connect-time choice.
  const context = useConnectionContextKey();
  const copy = useNamespaceCopy(pageCatalog);
  const [opened, setOpened] = useState<{
    audience: 'private' | 'company';
    context: string;
    canConnectCompany?: boolean;
  } | null>(null);
  const audience = opened?.context === context ? opened.audience : null;
  useEffect(() => {
    setOpened((current) => (current && current.context !== context ? null : current));
  }, [context]);
  const setAudience = (next: 'private' | 'company' | null) =>
    setOpened(next ? { audience: next, context } : null);
  return {
    /** Opens the sheet; the catalogue entry that opens it says whether its company option is allowed. */
    open: (next: 'private' | 'company', canConnectCompanyHere?: boolean) =>
      setOpened({ audience: next, context, canConnectCompany: canConnectCompanyHere }),
    dialog: audience ? (
      <ShopifyDevAppDialog
        key={context}
        initialAudience={audience}
        companyName={companyName}
        canConnectCompany={opened?.canConnectCompany ?? canConnectCompany}
        onClose={() => setAudience(null)}
        onCreated={(response, choice) => {
          setAudience(null);
          const read = response.connection.readAccess;
          void (read ? applyConnectChoice(read, choice) : Promise.resolve(null)).then((saved) =>
            onConnected(saved || !read ? copy.noticeStoreConnected : copy.noticeStoreChooseAccess),
          );
        }}
      />
    ) : null,
  };
}

export function ShopifyDevAppCard({
  entry,
  connection,
  busy,
  onOpen,
}: {
  entry: ShopifyDevAppEntry;
  /** A store already connected through the company's own Shopify app, when there is one. */
  connection: CatalogueConnection | null;
  /** Another connect is under way: the tile waits like every other. */
  busy: boolean;
  onOpen: (audience: 'private' | 'company') => void;
}) {
  const copy = useNamespaceCopy(pageCatalog);
  const available = entry.setup.available;
  // The store Your connections lists decides, whatever its state; the catalogue's own flag only
  // covers onboarding, where there are no rows.
  const state: CatalogueTileState = connection
    ? tileStateFor(connection.state)
    : entry.connected
      ? 'connected'
      : available
        ? 'connect'
        : 'unavailable';
  return (
    <CatalogueCard
      source="poneva"
      logo={entry.img_src}
      name={entry.name}
      subtitle={
        connection
          ? (connection.identity ?? copy.tileConnected)
          : entry.connected
            ? copy.tileConnected
            : formatCopy(copy.tileOwnApp, { name: 'Shopify' })
      }
      description={entry.description}
      state={state}
      action={
        connection
          ? connectedTileAction(state, connection, copy, busy)
          : entry.connected
            ? { label: copy.tileConnected }
            : {
                disabled: !available || busy,
                // The sheet chooses who can use the store; the company is offered when allowed.
                onClick: () => onOpen('private'),
                label: available ? copy.tileConnect : copy.tileUnavailable,
              }
      }
    />
  );
}
